/**
 * Shared behaviour for the NASTY global design system sections.
 *
 * Two primitives ported from the global site's React components, rebuilt without React:
 *
 *   Reveal        scroll entrance, from shared/Reveal.tsx
 *   EngraveCycle  the cycling headline word, from shared/EngraveCycle.tsx
 *
 * Both keep the source's measured timings rather than approximations, and both are additive: the
 * content they touch is already visible and already correct before this file runs. A reveal that
 * gates visibility ships a blank section anywhere the observer never fires, which is any hidden
 * tab, any headless renderer and any crawler that does not scroll.
 */

const QUINT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const reduced = matchMedia('(prefers-reduced-motion: reduce)');

/* ===== Reveal =====
   Reveal.tsx defaults: y 22px, duration 0.6s, ease quint, fires once at 20% visibility. Stagger
   runs children 0.08s apart after a 0.06s lead. */
const REVEAL_STAGGER = 80;
const REVEAL_LEAD = 60;

/*
 * The markup carries no hidden state. Content ships visible and this opts it into an entrance,
 * which is the only safe order: put `pending` in the Liquid and a failed fetch, a blocked module,
 * a headless render or a crawler gets an invisible section. That is not a hypothetical, it is what
 * this function did in its first draft and the static harness rendered an empty hero.
 *
 * Anything already on screen when this runs is marked done rather than animated. Animating it would
 * mean hiding something the user can already see, which is a flash, and an entrance for content
 * that never entered is meaningless anyway.
 */
function initReveal() {
  const targets = document.querySelectorAll('.ng-reveal:not([data-reveal])');
  if (!targets.length || reduced.matches) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.dataset.reveal = 'in';
        // `once: true` in the source. Unobserving also stops this working on every scroll.
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.2 }
  );

  for (const el of targets) {
    if (el.getBoundingClientRect().top < innerHeight) {
      el.dataset.reveal = 'in';
      continue;
    }

    // A group can stagger its children without each child carrying an inline delay.
    const group = el.closest('[data-reveal-group]');
    if (group && !el.style.getPropertyValue('--ng-reveal-delay')) {
      const index = [...group.querySelectorAll('.ng-reveal')].indexOf(el);
      if (index > 0) {
        el.style.setProperty('--ng-reveal-delay', `${REVEAL_LEAD + index * REVEAL_STAGGER}ms`);
      }
    }

    el.dataset.reveal = 'pending';
    observer.observe(el);
  }
}

/* ===== EngraveCycle =====
   One word of a headline is struck letter by letter, held, deleted, and replaced by the next word.
   Source timings: type 95ms per letter, delete 48ms per letter, hold 1700ms, gap 320ms. */
const TYPE_MS = 95;
const DELETE_MS = 48;
const HOLD_MS = 1700;
const GAP_MS = 320;

class NastyEngrave extends HTMLElement {
  connectedCallback() {
    this.words = (this.getAttribute('words') || '')
      .split(',')
      .map((w) => w.trim())
      .filter(Boolean);
    if (this.words.length === 0) return;

    this.live = this.querySelector('[data-engrave-live]');
    if (!this.live) return;

    /*
     * The first word is already in the markup as real text, so the heading reads correctly with no
     * script and is what assistive tech announces. Everything this class draws is aria-hidden, and
     * the sizers hold the cell open at the width of the longest word so a strike never reflows the
     * line.
     */
    if (reduced.matches) return;

    /*
     * Start on word zero fully drawn, matching the static markup. Starting at zero letters made the
     * heading drop its word for the whole 1700ms hold before the first delete, which read as the
     * word going missing on load.
     */
    this.index = 0;
    this.letters = this.words[0].length;
    this.phase = 'hold';
    this.chars = [];

    this.caret = document.createElement('span');
    this.caret.className = 'ng-engrave-caret';

    this.live.setAttribute('aria-hidden', 'true');
    this.live.textContent = '';
    this.live.append(this.caret);

    this.paint();
    this.timer = setTimeout(() => this.step(), HOLD_MS);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  /**
   * Touches only the character that changed.
   *
   * This is a correctness requirement, not an optimisation. The first version rebuilt the whole word
   * every tick, and because `.ng-engrave-char` carries a 210ms strike that begins at opacity 0,
   * recreating the earlier characters restarted their animation too. The result was the entire word
   * flickering every 95ms rather than one character being struck.
   */
  paint() {
    const word = this.words[this.index];

    while (this.chars.length > this.letters) {
      this.chars.pop().remove();
    }

    while (this.chars.length < this.letters) {
      const span = document.createElement('span');
      span.className = 'ng-engrave-char';
      span.textContent = word[this.chars.length];
      this.live.insertBefore(span, this.caret);
      this.chars.push(span);
    }

    // A finished word should read as type, not as a prompt still waiting for input.
    this.caret.hidden = this.letters >= word.length && this.phase !== 'delete';
  }

  step() {
    const word = this.words[this.index];

    if (this.phase === 'hold') {
      this.phase = 'delete';
    }

    if (this.phase === 'delete') {
      // Stop at one letter, not zero. The gap used to be spent fully empty, which is invisible when
      // the cycling word trails its heading ("Something ") but reads as a broken sentence when it
      // leads one: the social headline rendered as ", not claimed." for 320ms on every cycle.
      if (this.letters > 1) {
        this.letters -= 1;
        this.paint();
        this.timer = setTimeout(() => this.step(), DELETE_MS);
        return;
      }

      // Swap the surviving letter for the next word's first, so the slot is never blank.
      this.index = (this.index + 1) % this.words.length;
      this.phase = 'type';
      this.letters = 0;
      this.paint();
      this.letters = 1;
      this.paint();
      this.timer = setTimeout(() => this.step(), GAP_MS);
      return;
    }

    if (this.letters < word.length) {
      this.letters += 1;
      this.paint();
      this.timer = setTimeout(() => this.step(), TYPE_MS);
      return;
    }

    this.phase = 'hold';
    this.paint();
    this.timer = setTimeout(() => this.step(), HOLD_MS);
  }
}

if (!customElements.get('nasty-engrave')) {
  customElements.define('nasty-engrave', NastyEngrave);
}

initReveal();

// Sections can arrive after first paint in the theme editor, so re-scan when one is replaced.
document.addEventListener('shopify:section:load', initReveal);

export { QUINT };
